import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import config from '../../../config';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// ─── Inline styles ─────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.75)',
    backdropFilter: 'blur(4px)', zIndex: 9999,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    background: '#ffffff', borderRadius: '16px', width: '100%', maxWidth: '900px',
    maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
    boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
  },
  header: {
    padding: '20px 24px 16px',
    borderBottom: '1px solid #e2e8f0',
    background: 'linear-gradient(135deg, #1e3a5f 0%, #2d6a9f 100%)',
    color: '#fff',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    borderRadius: '16px 16px 0 0',
  },
  headerTitle: { margin: 0, fontSize: '20px', fontWeight: 700 },
  headerSub: { margin: '4px 0 0', fontSize: '13px', opacity: 0.8 },
  closeBtn: {
    background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
    width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
    fontSize: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  body: { flex: 1, overflowY: 'auto', padding: '20px 24px' },
  summaryRow: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px',
    marginBottom: '20px',
  },
  card: {
    background: '#f8fafc', borderRadius: '12px', padding: '14px 16px',
    borderLeft: '4px solid #3b82f6', textAlign: 'center',
  },
  cardLabel: { fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
  cardValue: { fontSize: '24px', fontWeight: 700, margin: '4px 0 0', display: 'block' },
  tabs: {
    display: 'flex', gap: '4px', marginBottom: '20px',
    borderBottom: '2px solid #e2e8f0', paddingBottom: '0',
  },
  tab: {
    padding: '10px 20px', border: 'none', background: 'none',
    cursor: 'pointer', fontSize: '14px', fontWeight: 500, color: '#64748b',
    borderBottom: '2px solid transparent', marginBottom: '-2px', borderRadius: '8px 8px 0 0',
    transition: 'all 0.2s',
  },
  tabActive: {
    color: '#2d6a9f', borderBottomColor: '#2d6a9f', background: '#eff6ff',
  },
  chartsGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px',
  },
  chartCard: {
    background: '#f8fafc', borderRadius: '12px', padding: '16px',
    border: '1px solid #e2e8f0',
  },
  chartCardTitle: {
    fontSize: '13px', fontWeight: 600, color: '#374151', margin: '0 0 4px',
  },
  chartStats: {
    display: 'flex', gap: '12px', marginBottom: '8px',
    fontSize: '11px', color: '#6b7280',
  },
  chartWrap: { height: '180px', position: 'relative' },
  microGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px',
    marginTop: '16px',
  },
  microCard: {
    background: '#f8fafc', borderRadius: '10px', padding: '12px',
    border: '1px solid #e2e8f0', textAlign: 'center',
  },
  sectionTitle: { fontSize: '15px', fontWeight: 600, color: '#1e293b', margin: '20px 0 12px' },
  voiceStatsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px',
  },
  voiceStat: {
    background: '#f0f9ff', borderRadius: '10px', padding: '12px',
    border: '1px solid #bae6fd', textAlign: 'center',
  },
  footer: {
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex', justifyContent: 'flex-end',
    background: '#f8fafc', borderRadius: '0 0 16px 16px',
  },
  closeFinalBtn: {
    background: '#1e3a5f', color: '#fff', border: 'none',
    padding: '10px 28px', borderRadius: '8px', fontWeight: 600,
    cursor: 'pointer', fontSize: '14px',
  },
  center: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '60px 20px', gap: '12px', color: '#64748b',
  },
  noDataBadge: {
    background: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px',
    padding: '10px 16px', fontSize: '13px', color: '#854d0e',
    textAlign: 'center', margin: '8px 0',
  },
};

// ─── Chart colour palette ──────────────────────────────────────────
const PALETTE = {
  eyeContact:       { border: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  blink:            { border: '#ec4899', bg: 'rgba(236,72,153,0.12)' },
  facialTension:    { border: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  smile:            { border: '#eab308', bg: 'rgba(234,179,8,0.12)' },
  headTilt:         { border: '#84cc16', bg: 'rgba(132,204,22,0.12)' },
  pitch:            { border: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
  speechRate:       { border: '#f97316', bg: 'rgba(249,115,22,0.12)' },
  pauses:           { border: '#ec4899', bg: 'rgba(236,72,153,0.12)' },
  warmth:           { border: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  tremor:           { border: '#6366f1', bg: 'rgba(99,102,241,0.12)' },
  heartRate:        { border: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  respiratory:      { border: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  hrv:              { border: '#8b5cf6', bg: 'rgba(139,92,246,0.12)' },
  energy:           { border: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmtTime = (ts) => new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
const fmtPct  = (v)  => `${Math.round((v || 0) * 100)}%`;
const fmtWPM  = (v)  => `${Math.round(v || 0)} WPM`;
const fmtBPM  = (v)  => `${Math.round(v || 0)}/min`;

const avg = (arr) => {
  if (!arr || arr.length === 0) return 0;
  return arr.reduce((s, d) => s + (d.value || 0), 0) / arr.length;
};

const calcStats = (arr) => {
  if (!arr || arr.length === 0) return null;
  const vals = arr.map(d => d.value);
  const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
  return { avg: mean, min: Math.min(...vals), max: Math.max(...vals), count: vals.length };
};

// ─── Chart builder ───────────────────────────────────────────────────────────
const buildDatasetFromValues = (labels, values, label, colour) => ({
  labels: labels || values?.map((_, i) => `Min ${i + 1}`),
  datasets: [{
    label,
    data: values,
    borderColor: colour.border,
    backgroundColor: colour.bg,
    borderWidth: 2.5,
    pointRadius: 4,
    pointHoverRadius: 6,
    pointBackgroundColor: colour.border,
    tension: 0.4,
    fill: true,
  }],
});

const chartOpts = (yMax = 1, yLabel = '', isBPM = false, isWPM = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1e293b',
      titleColor: '#94a3b8',
      bodyColor: '#f1f5f9',
      padding: 10,
      callbacks: {
        label: ctx => {
          const v = ctx.parsed.y;
          if (isBPM) return ` ${v.toFixed(1)} /min`;
          if (isWPM) return ` ${v.toFixed(0)} WPM`;
          return ` ${(v * 100).toFixed(1)}%`;
        },
      },
    },
  },
  scales: {
    x: {
      grid: { color: '#f1f5f9' },
      ticks: { font: { size: 10 }, color: '#94a3b8', maxTicksLimit: 6 },
    },
    y: {
      beginAtZero: true,
      max: yMax,
      grid: { color: '#f1f5f9' },
      title: { display: !!yLabel, text: yLabel, font: { size: 11 }, color: '#94a3b8' },
      ticks: {
        font: { size: 10 }, color: '#94a3b8',
        callback: v => isBPM ? v : isWPM ? v : `${(v * 100).toFixed(0)}%`,
      },
    },
  },
});

// ─── Score colour ─────────────────────────────────────────────────────────────
const scoreColor = (s) => s >= 70 ? '#10b981' : s >= 50 ? '#f59e0b' : '#ef4444';

// ─── Mini stat card ───────────────────────────────────────────────────────────
const StatCard = ({ label, value, accent = '#3b82f6' }) => (
  <div style={{ ...S.card, borderLeftColor: accent }}>
    <div style={S.cardLabel}>{label}</div>
    <span style={{ ...S.cardValue, color: accent }}>{value}</span>
  </div>
);

// ─── Chart section ────────────────────────────────────────────────────────────
const ChartSection = ({ title, children, stats }) => (
  <div style={S.chartCard}>
    <div style={S.chartCardTitle}>{title}</div>
    {stats && <div style={S.chartStats}>{stats}</div>}
    <div style={S.chartWrap}>{children}</div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
const DetailedAnalyticsModal = ({ interviewId, token, candidateId, onClose }) => {
  const [loading,   setLoading]   = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [error,     setError]     = useState(null);
  const [tab,       setTab]       = useState('camera');

  useEffect(() => {
    fetchAnalyticsSummary();
  }, [interviewId]);

  useEffect(() => {
    return () => {
      window.dispatchEvent(new CustomEvent('analyticsModalClosed'));
    };
  }, []);

  const fetchAnalyticsSummary = async () => {
    setLoading(true);
    setError(null);
      try {
        let url = `${config.baseURL}/api/teams/interview/${interviewId}/analytics/summary`;
      if (candidateId) {
        url += `/${candidateId}`;
      }
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success && data.summary) {
        setSummaryData(data.summary);
      } else {
        setError('No analytics data available yet.');
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError(`Failed to load analytics: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    window.dispatchEvent(new CustomEvent('analyticsModalClosed'));
    onClose();
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <div style={S.center}>
          <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#2d6a9f', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <p>Loading analytics data…</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !summaryData) return (
    <div style={S.overlay} onClick={handleClose}>
      <div style={{ ...S.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
        <div style={{ ...S.header, background: '#1e293b' }}>
          <h2 style={S.headerTitle}>📊 Interview Analytics</h2>
          <button style={S.closeBtn} onClick={handleClose}>×</button>
        </div>
        <div style={S.center}>
          <span style={{ fontSize: 40 }}>📭</span>
          <p style={{ textAlign: 'center', lineHeight: 1.6 }}>{error || 'No analytics data found.'}</p>
          <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
            Analytics are saved when the candidate ends the interview.
          </p>
        </div>
        <div style={S.footer}>
          <button style={S.closeFinalBtn} onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>
  );

  // ── Data ─────────────────────────────────────────────────────────────────
  const { metrics, chartData, overallScore, interpretation, healthRecommendations } = summaryData;
  const overall = Math.round(overallScore || 50);
  const health = metrics?.health || {};
  const healthCharts = chartData?.health || {};

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={S.header}>
          <div>
            <h2 style={S.headerTitle}>📊 Detailed Interview Analytics</h2>
            <p style={S.headerSub}>Candidate Performance Analysis</p>
          </div>
          <button style={S.closeBtn} onClick={handleClose}>×</button>
        </div>

        <div style={S.body}>
          {/* Summary cards */}
          <div style={S.summaryRow}>
            <StatCard label="Overall Score" value={`${overall}%`} accent={scoreColor(overall)} />
            <StatCard label="Eye Contact"   value={fmtPct(metrics?.avgEyeContact)}  accent="#f97316" />
            <StatCard label="Speech Rate"   value={fmtWPM(metrics?.avgSpeechRate)}  accent="#ec4899" />
            <StatCard label="Interruptions" value={metrics?.totalInterruptions || 0} accent="#8b5cf6" />
          </div>

          {/* Tabs */}
          <div style={S.tabs}>
            {['camera', 'voice', 'health'].map(t => (
              <button
                key={t}
                style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }}
                onClick={() => setTab(t)}
              >
                {t === 'camera' ? '📷 Camera Analytics' : t === 'voice' ? '🎤 Voice Analytics' : '❤️ Health Metrics'}
              </button>
            ))}
          </div>

          {/* Camera Tab */}
          {tab === 'camera' && (
            <>
              <div style={S.chartsGrid}>
                <ChartSection title="👁️ Eye Contact Over Time">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.camera?.eyeContact?.labels,
                      chartData?.camera?.eyeContact?.values,
                      'Eye Contact',
                      PALETTE.eyeContact
                    )}
                    options={chartOpts(1, 'Eye Contact')}
                  />
                </ChartSection>

                <ChartSection title="😉 Blink Rate Over Time">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.camera?.blinkRate?.labels,
                      chartData?.camera?.blinkRate?.values,
                      'Blink Rate',
                      PALETTE.blink
                    )}
                    options={chartOpts(30, 'Blinks/min', true)}
                  />
                </ChartSection>

                <ChartSection title="😐 Facial Tension">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.camera?.facialTension?.labels,
                      chartData?.camera?.facialTension?.values,
                      'Facial Tension',
                      PALETTE.facialTension
                    )}
                    options={chartOpts(1, 'Tension')}
                  />
                </ChartSection>

                <ChartSection title="😊 Smile Authenticity">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.camera?.smileAuthenticity?.labels,
                      chartData?.camera?.smileAuthenticity?.values,
                      'Smile',
                      PALETTE.smile
                    )}
                    options={chartOpts(1, 'Authenticity')}
                  />
                </ChartSection>
              </div>

              {/* Interpretation */}
              {(interpretation?.strengths?.length > 0 || interpretation?.improvements?.length > 0) && (
                <>
                  <div style={S.sectionTitle}>📋 Analysis</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    {interpretation.strengths?.length > 0 && (
                      <div style={{ background: '#ecfdf5', padding: '12px', borderRadius: '10px', borderLeft: '3px solid #10b981' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#065f46', marginBottom: '8px' }}>✅ Strengths</div>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#374151' }}>
                          {interpretation.strengths.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                    {interpretation.improvements?.length > 0 && (
                      <div style={{ background: '#fffbeb', padding: '12px', borderRadius: '10px', borderLeft: '3px solid #f59e0b' }}>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>🎯 Areas to Improve</div>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#374151' }}>
                          {interpretation.improvements.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {/* Voice Tab */}
          {tab === 'voice' && (
            <>
              <div style={S.chartsGrid}>
                <ChartSection title="🎵 Pitch Variability">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.voice?.pitchVariability?.labels,
                      chartData?.voice?.pitchVariability?.values,
                      'Pitch Variability',
                      PALETTE.pitch
                    )}
                    options={chartOpts(1, 'Variability')}
                  />
                </ChartSection>

                <ChartSection title="🗣️ Speech Rate">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.voice?.speechRate?.labels,
                      chartData?.voice?.speechRate?.values,
                      'Speech Rate',
                      PALETTE.speechRate
                    )}
                    options={chartOpts(200, 'WPM', false, true)}
                  />
                </ChartSection>

                <ChartSection title="⏸️ Pause Duration">
                  <Line
                    data={buildDatasetFromValues(
                      chartData?.voice?.pauses?.labels,
                      chartData?.voice?.pauses?.values,
                      'Pauses',
                      PALETTE.pauses
                    )}
                    options={chartOpts(0.5, 'Pause Level')}
                  />
                </ChartSection>
              </div>

              {/* Voice summary */}
              <div style={S.sectionTitle}>📈 Voice Summary</div>
              <div style={S.voiceStatsGrid}>
                {[
                  { label: 'Interruptions',    value: metrics?.totalInterruptions || 0 },
                  { label: 'Avg Speech Rate',  value: fmtWPM(metrics?.avgSpeechRate) },
                  { label: 'Pitch Variability',value: fmtPct(metrics?.avgPitchVariability) },
                  { label: 'Vocal Tremor',     value: fmtPct(metrics?.avgVocalTremor) },
                  { label: 'Warmth Resonance', value: fmtPct(metrics?.avgWarmthResonance) },
                ].map(({ label, value }) => (
                  <div key={label} style={S.voiceStat}>
                    <div style={{ fontSize: 11, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1e3a5f', marginTop: 4 }}>{value}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Health Tab */}
          {tab === 'health' && (
            <>
              {/* Health Score Cards */}
              <div style={S.summaryRow}>
                <StatCard 
                  label="Overall Health" 
                  value={fmtPct(health.overall_health_score)} 
                  accent="#10b981" 
                />
                <StatCard 
                  label="Stress Level" 
                  value={health.stress_label || 'NA'} 
                  accent={health.stress_label === 'high' ? '#ef4444' : health.stress_label === 'moderate' ? '#f59e0b' : '#10b981'} 
                />
                <StatCard 
                  label="Cardiovascular" 
                  value={fmtPct(health.cardiovascular_score)} 
                  accent="#3b82f6" 
                />
                <StatCard 
                  label="Recovery" 
                  value={fmtPct(health.recovery_score)} 
                  accent="#8b5cf6" 
                />
              </div>

              {/* Health Charts */}
              <div style={S.chartsGrid}>
                <ChartSection title="❤️ Heart Rate Over Time">
                  <Line
                    data={buildDatasetFromValues(
                      healthCharts?.heartRate?.labels,
                      healthCharts?.heartRate?.values,
                      'Heart Rate',
                      PALETTE.heartRate
                    )}
                    options={chartOpts(120, 'BPM', true)}
                  />
                </ChartSection>
                
                <ChartSection title="🌬️ Respiratory Rate">
                  <Line
                    data={buildDatasetFromValues(
                      healthCharts?.respiratoryRate?.labels,
                      healthCharts?.respiratoryRate?.values,
                      'Respiratory Rate',
                      PALETTE.respiratory
                    )}
                    options={chartOpts(25, 'Breaths/min', true)}
                  />
                </ChartSection>
                
                <ChartSection title="📊 Heart Rate Variability (HRV)">
                  <Line
                    data={buildDatasetFromValues(
                      healthCharts?.hrv?.labels,
                      healthCharts?.hrv?.values,
                      'HRV',
                      PALETTE.hrv
                    )}
                    options={chartOpts(100, 'ms', true)}
                  />
                </ChartSection>
                
                <ChartSection title="⚡ Active Energy">
                  <Line
                    data={buildDatasetFromValues(
                      healthCharts?.activeEnergy?.labels,
                      healthCharts?.activeEnergy?.values,
                      'Active Energy',
                      PALETTE.energy
                    )}
                    options={chartOpts(10, 'kcal/min', false)}
                  />
                </ChartSection>
              </div>

              {/* Health Scores Details */}
              <div style={S.sectionTitle}>📈 Health Scores Details</div>
              <div style={S.voiceStatsGrid}>
                {[
                  { label: 'Cardiovascular', value: fmtPct(health.cardiovascular_score), color: '#3b82f6' },
                  { label: 'Respiratory',    value: fmtPct(health.respiratory_score), color: '#06b6d4' },
                  { label: 'Recovery',       value: fmtPct(health.recovery_score), color: '#8b5cf6' },
                  { label: 'Energy',         value: fmtPct(health.energy_score), color: '#f59e0b' },
                ].map(({ label, value, color }) => (
                  <div key={label} style={{ ...S.voiceStat, background: `${color}10`, borderColor: `${color}30` }}>
                    <div style={{ fontSize: 11, color: color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#1e3a5f', marginTop: 4 }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Health Recommendations */}
              {healthRecommendations && healthRecommendations.length > 0 && (
                <>
                  <div style={S.sectionTitle}>💡 Health Recommendations</div>
                  <div style={S.voiceStatsGrid}>
                    {healthRecommendations.map((rec, idx) => (
                      <div key={idx} style={{ ...S.voiceStat, textAlign: 'left', background: '#fef3c7', borderColor: '#f59e0b' }}>
                        <div style={{ fontSize: 13, color: '#92400e' }}>• {rec}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div style={S.footer}>
          <button style={S.closeFinalBtn} onClick={handleClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default DetailedAnalyticsModal;